<template>
  <v-card class="notification-card" :style="cardStyle">
    <v-card-title class="notification-header" :style="headerStyle">
      <div class="d-flex align-center justify-space-between w-100">
        <div class="d-flex align-center">
          <v-icon class="me-2" icon="mdi-bell-outline" />
          {{ title }}
        </div>

        <v-chip size="small" :color="color ?? 'primary'" variant="tonal">
          {{ notifications.length }}
        </v-chip>
      </div>
    </v-card-title>

    <v-divider />

    <v-card-text v-if="notifications.length" class="notification-body pa-0">
      <v-list density="comfortable">
        <v-list-item
          class="notification-item cursor-pointer"
          v-for="notification in notifications"
          :key="notification.id"
          @click="goToEntity(notification)"
        >
          <template #prepend>
            <v-icon icon="mdi-bell-outline" />
          </template>

          <v-list-item-title class="font-weight-medium">
            {{ notification.name }}
          </v-list-item-title>

          <v-list-item-subtitle>
            {{ notification.desc }}
          </v-list-item-subtitle>

          <template #append>
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              @click.stop="remove(notification)"
            />
          </template>
        </v-list-item>
      </v-list>
    </v-card-text>

    <v-card-text v-else class="text-medium-emphasis text-center notification-body">
      Aucune notification disponible
    </v-card-text>
  </v-card>

  <confirm-dialog
    v-model="deleteNotificationDialog"
    :dialogTitle="dialogTitle"
    :dialogText="dialogText"
    @confirm="onConfirm"
    @cancel="onCancel"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { Notification, NotificationType } from "@/types";
import { useAppStore } from "@/store";
import { deleteNotification, getNotificationsByType } from "@/utils/notification-api";
import ConfirmDialog from "../ConfirmDialog.vue";

interface Props {
  notificationType: NotificationType;
  title: string;
  color?: string;
  height?: string;
  maxHeight?: string;
}

const props = defineProps<Props>();

const color = props.color;

const loading = ref(false);
const deleteNotificationDialog = ref(false);
const dialogTitle = ref("Suppression de la notification");
const dialogText = ref("Vouez-vous supprimer cette notification?");
const notificationToDelete = ref<Notification | null>(null);
const notifications = ref<Notification[]>([]);

const router = useRouter();
const appStore = useAppStore();

const cardStyle = computed(() => ({
  height: props.height,
  maxHeight: props.maxHeight,
}));

const headerStyle = computed(() => ({
  borderLeft: color ? `4px solid ${color}` : undefined,
}));

onMounted(() => {
  fetchNotifications(props.notificationType);
});

const fetchNotifications = async (notificationType: NotificationType) => {
  try {
    loading.value = true;
    notifications.value = await getNotificationsByType(notificationType);
  } catch (error) {
    // Ignore
  } finally {
    loading.value = false;
  }
};

const onConfirm = async () => {
  if (!notificationToDelete.value) {
    return;
  }

  try {
    loading.value = true;
    deleteNotificationDialog.value = false;

    await deleteNotification(notificationToDelete.value.id);
    await fetchNotifications(props.notificationType);

    appStore.sendSuccessNotice("Notification has been deleted.");
  } catch (error) {
    appStore.sendErrorNotice("Failed to delete notification.");
  } finally {
    loading.value = false;
    notificationToDelete.value = null;
  }
};

const onCancel = () => {
  notificationToDelete.value = null;
  deleteNotificationDialog.value = false;
};

function remove(notification: Notification) {
  notificationToDelete.value = notification;
  deleteNotificationDialog.value = true;
}

function goToEntity(notification: { relatedEntity: string }) {
  if (notification.relatedEntity) {
    router.push(notification.relatedEntity);
  }
}
</script>

<style scoped>
.notification-card {
  height: 320px;
  display: flex;
  flex-direction: column;
}

.notification-header {
  padding-left: 12px;
}

.notification-body {
  flex: 1;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}

.notification-item {
  margin: 4px 8px;
  border-radius: 12px;
  background-color: rgba(0, 0, 0, 0.03);
}

.notification-item:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

@media (prefers-color-scheme: dark) {
  .notification-item {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .notification-item:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
}
</style>
